import type { TSocialOfferSpotlight } from "@api";
import type { ISocialOffer } from "@bbj-types/offer";
import { DateRangePickerTextField, DateRangePickerViews } from "@inputs/date/DateRangePicker";
import CloseIcon from "@mui/icons-material/Close";
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import type { IDrawerSocialOfferHeaderProps } from "..";
import { useConfirmAddSocialOfferSpotlight } from "./logics";
import { SpotlightDialog, classes } from "./styles";

interface IConfirmationAddSocialOfferSpotlightModal<T extends ISocialOffer> {
  offer: T;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  updateSocialOffersSpotlight?: Dispatch<SetStateAction<TSocialOfferSpotlight[] | undefined>>;
  onClose: () => void;
  onUpdateOffer: IDrawerSocialOfferHeaderProps<T>["onUpdateOffer"];
}

export const ConfirmationAddSocialOfferSpotlightModal = <T extends ISocialOffer>({
  openModal,
  setOpenModal,
  offer,
  onClose,
  onUpdateOffer,
}: IConfirmationAddSocialOfferSpotlightModal<T>) => {
  const { handleClickAddSpotilght, rangeSpotlight, setRangeSpotlight, loading, rangeError, errorRange } =
    useConfirmAddSocialOfferSpotlight(offer, onClose, onUpdateOffer);

  return (
    <SpotlightDialog className={classes.root} disableEscapeKeyDown open={openModal}>
      <DialogTitle component={Stack} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" noWrap>
          Mettre une offre à la une
        </Typography>
        <IconButton size="small" onClick={() => setOpenModal(false)}>
          <CloseIcon fontSize="medium" color="primary" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography>
            Vous êtes sur le point de mettre cette offre à la une. Elle apparaîtra par défaut sur la page des offres de
            service ou dans l’onglet “Offres de service” d’une fiche candidat, tant qu'aucun filtre ne sera activé.
          </Typography>
          <Typography>
            Déterminez ci-dessous la durée pendant laquelle cette offre apparaîtra dans la liste des offres à la une :
          </Typography>
          <DateRangePickerTextField
            label="Période de mise à la une * :"
            placeholder="Saisissez une période"
            maxDate={offer.serviceEndDate}
            required={{ startDate: true, endDate: true }}
            onChange={(range) => setRangeSpotlight(range)}
            value={rangeSpotlight ?? { startDate: null, endDate: null }}
            defaultView={DateRangePickerViews.DAY}
            error={rangeError || errorRange}
            helperText={
              rangeError
                ? "La période de mise à la une doit être comprise dans la période de validité de l'offre"
                : undefined
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.confirmActions}>
        <Button
          onClick={() => setOpenModal(false)}
          variant="outlined"
          name="cancel"
          aria-labelledby="Abandonner"
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          disabled={!rangeSpotlight || rangeError}
          loading={loading}
          onClick={() => handleClickAddSpotilght()}
          color="primary"
          variant="contained"
          name="confirm"
          aria-labelledby="Valied"
        >
          Enregistrer
        </Button>
      </DialogActions>
    </SpotlightDialog>
  );
};
